import { UserFieldsFragment as ReplyListUser } from '__generated__/graphql/legacy/graphql';
import classNames from 'classnames/bind';
import { features } from 'constants/featureFlags';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { relativeFormattedDateTime } from 'helpers/dateHelpers';
import { getReactors } from 'helpers/reactionHelpers';
import { TFunction } from 'next-i18next';
import { PostAction, PostBody, Reply } from 'types/post';
import {
  EVENT_POST_TYPES,
  NUMBER_OF_REACTIONS,
  REACTION_TYPES,
  SUCCESS_TOAST,
} from 'utils/constants';
import { rawId } from 'utils/rawId';
import { genericToastAlert, successToastMessage } from 'utils/toastHelpers';
import { canDeleteComment, canEditPost, canPostInReadOnlySpace } from 'utils/userHelpers';

import React, { MutableRefObject, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Mutation } from '@apollo/client/react/components';

import { Button } from '@thinkific/chalk/Button';
import { Tooltip } from '@thinkific/chalk/Tooltip';
import { ArrowArcLeft } from '@thinkific/chalk/react-icons';

import { useCommunityContext } from 'components/CommunityContext';
import ExpandableContent from 'components/Post/components/ExpandableContent';
import ReactionButton from 'components/ReactionButton';
import { useReplyEditorContext } from 'components/ReplyEditorContext/ReplyEditorContext';
import UserDetail from 'components/UserDetail';
import ConfirmDeleteModal from 'components/new/ConfirmDeleteModal';
import PostActions from 'components/new/Posts/PostActions';
import ReactionBar from 'components/new/Posts/ReactionBar';
import ReactionSummaryDisplay from 'components/new/Posts/ReactionSummary';
import { useReactionHandler } from 'components/new/Posts/hooks/useReactionHandler';
import { DANGER_TYPE } from 'components/new/utils/constants';

import { Attachment } from 'schema/Post/types';
import {
  DELETE_REPLY_MUTATION,
  FOLLOW_REPLY_MUTATION,
  UNFOLLOW_REPLY_MUTATION,
} from 'schema/Replies/mutations';

import ReplyThread from '../ReplyThread/ReplyThread';
import styles from './Comment.module.scss';

const cx = classNames.bind(styles);

interface CommentProps {
  comment: Reply;
  commentRef?: MutableRefObject<HTMLDivElement | null> | null;
  body?: PostBody;
  fetchMoreReplies?: () => void;
  hasReplies?: boolean;
  isFollowingThread?: boolean;
  isReply?: boolean;
  parentPostId: string;
  parentReplyId?: string | null;
  t: TFunction;
  totalReplyCount?: number;
  user: ReplyListUser;
  onCommentDeleted?: () => void;
  onPostChange?: () => void;
  isReadOnlySpace?: boolean;
}

const Comment = ({
  comment,
  commentRef = null,
  hasReplies = false,
  isFollowingThread = false,
  isReply = false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onCommentDeleted = () => {},
  parentPostId,
  parentReplyId = null,
  t,
  totalReplyCount = 0,
  user,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onPostChange = () => {},
  fetchMoreReplies,
  isReadOnlySpace = false,
}: CommentProps) => {
  const { currentUser, trackEvent, featureFlags } = useCommunityContext();
  const multipleReactionsEnabled = featureFlags[features.COMMUNITY_REACTIONS] || false;
  const { updateReplyData, startEditing } = useReplyEditorContext();

  // Manage reactions with unified hook
  const { reactionStats, myReactions, handleReactionClick } = useReactionHandler({
    id: comment.id,
    parentPostId,
    parentReplyId: parentReplyId || undefined,
    initialReactionStats: comment?.reactionStats || undefined,
    initialMyReactions: comment?.myReactions || undefined,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [unfollowReply, { loading: unfollowLoading }] = useMutation(UNFOLLOW_REPLY_MUTATION);
  const [followReply, { loading: followLoading }] = useMutation(FOLLOW_REPLY_MUTATION);

  const { ATTACHMENT, COMMENT_ID, COMMENT_ID_ENCODED, POST_TYPE, POST_ID, POST_ID_ENCODED } =
    TrackingProperty;

  const trackCommentEvent = (event) =>
    trackEvent(event, {
      [ATTACHMENT]: hasAttachment ? comment?.postAttachments?.[0].attachmentType : null,
      [COMMENT_ID]: rawId(comment.id),
      [COMMENT_ID_ENCODED]: comment.id,
      [POST_ID]: isReply ? rawId(parentReplyId || '') : rawId(parentPostId),
      [POST_ID_ENCODED]: isReply ? parentReplyId : parentPostId,
      [POST_TYPE]: isReply ? EVENT_POST_TYPES.REPLY : EVENT_POST_TYPES.COMMENT,
    });

  const creationDateTime = relativeFormattedDateTime(comment.createdAt, currentUser?.locale, t);

  const canUserDeleteComment = canDeleteComment(currentUser, user);
  const canUserEditPost = canEditPost(currentUser, user);

  const handleDeleteComment = () => {
    document.body.style.overflow = 'hidden';
    setIsOpen(true);
  };

  const closeModal = () => {
    document.body.style.overflow = 'visible';
    setIsOpen(false);
  };

  const handleConfirmDelete = (mutation) => async () => {
    try {
      const { data: deleteReplyData, errors: deleteReplyError } = await mutation({
        variables: {
          id: comment.id,
        },
        update(cache) {
          // Because it updates the state, closeModal() needs to be called before we update
          // the cache, as that will result in unmounting the component.
          closeModal();

          const commentCacheId = cache.identify({
            __typename: 'Reply',
            id: comment.id,
          });
          const parentCacheId = cache.identify({
            __typename: parentReplyId ? 'Reply' : 'Post',
            id: parentReplyId || parentPostId,
          });
          cache.modify({
            id: parentCacheId,
            fields: {
              replies(
                existingReplies = {
                  totalCount: undefined,
                }
              ) {
                return {
                  ...existingReplies,
                  totalCount: (existingReplies.totalCount || 0) - 1,
                };
              },
            },
          });
          cache.evict({ id: commentCacheId });
          cache.gc();
        },
      });
      onCommentDeleted();
      onPostChange?.();
      trackCommentEvent(TrackingEvent.DELETE_POST);

      const userErrors = deleteReplyData?.deleteReply?.userErrors;
      if (!userErrors?.length && !deleteReplyError?.length) {
        successToastMessage(
          isReply ? t(SUCCESS_TOAST.DELETE_REPLY) : t(SUCCESS_TOAST.DELETE_COMMENT)
        );
      } else {
        genericToastAlert();
      }
    } catch {
      genericToastAlert();
    }
  };

  const handleEditComment = () => {
    startEditing(comment);
  };

  const onReplyClick = () => {
    updateReplyData(comment.id, comment.content || user.fullName || '');
  };

  const commentActionsList: PostAction[] = [];
  const commentId = isReply ? parentReplyId : comment.id;
  const isFollowingComment = isReply ? isFollowingThread : comment.isFollowing;

  if (isFollowingComment && !unfollowLoading) {
    commentActionsList.push({
      name: 'unfollow',
      text: isReply ? t('Unfollow thread') : t('Unfollow comment'),
      onClick: async () => {
        await unfollowReply({
          variables: {
            id: commentId,
            numberOfReactions: NUMBER_OF_REACTIONS,
            multipleReactionsEnabled,
          },
        });
        trackCommentEvent(TrackingEvent.UNFOLLOW_COMMENT);
      },
    });
  } else if (!followLoading) {
    commentActionsList.push({
      name: 'follow',
      text: isReply ? t('Follow thread') : t('Follow comment'),
      onClick: async () => {
        await followReply({
          variables: {
            id: commentId,
            numberOfReactions: NUMBER_OF_REACTIONS,
            multipleReactionsEnabled,
          },
        });
        trackCommentEvent(TrackingEvent.FOLLOW_COMMENT);
      },
    });
  }

  const confirmDeleteBody = isReply
    ? t(
        'components-comment-delete_reply_confirmation',
        'Are you sure you want to delete this reply? You cannot undo this action.'
      )
    : t(
        'components-comment-delete_comment_confirmation',
        'Are you sure you want to delete this comment? You cannot undo this action.'
      );

  if (canUserEditPost) {
    commentActionsList.push({
      name: 'edit',
      title: t('common-edit', 'Edit'),
      text: t('common-edit', 'Edit'),
      onClick: handleEditComment,
    });
  }

  const confirmDeleteTitle = isReply ? t('Delete reply') : t('Delete comment');

  if (canUserDeleteComment) {
    commentActionsList.push({
      name: 'delete',
      title: confirmDeleteTitle,
      text: confirmDeleteTitle,
      onClick: handleDeleteComment,
      type: DANGER_TYPE,
    });
  }

  const hasAttachment = comment.postAttachments && comment.postAttachments[0];

  const reactionComponent = multipleReactionsEnabled ? (
    <ReactionBar onReactionClick={handleReactionClick} />
  ) : (
    <ReactionButton
      hasReacted={comment.hasReacted}
      id={comment.id}
      isReactingTo="comment"
      parentPostId={parentPostId}
      parentReplyId={parentReplyId || ''}
      postReactionCount={comment.postReactions ? comment.postReactions.totalCount : 0}
      reactionType={REACTION_TYPES.LIKE_POST}
      reactors={getReactors(comment)}
      t={t}
    />
  );

  const replyButton = ({ disabled = false }: { disabled?: boolean }) => {
    return (
      <Button
        className={cx('comment-list__reply-btn')}
        disabled={disabled}
        emphasis="low"
        icon={ArrowArcLeft}
        iconPosition="lead"
        size="sm"
        onClick={onReplyClick}
      >
        {t('Reply', 'Reply')}
      </Button>
    );
  };

  const replyButtonDisabled = isReadOnlySpace && !canPostInReadOnlySpace(currentUser);

  const commentContent = (
    <>
      <div className={cx('comment__detail')}>
        <div className={cx('comment__user-details')}>
          <UserDetail
            author={user}
            editedAt={comment.editedAt}
            isFollowing={comment.isFollowing}
            isReply={isReply}
            time={creationDateTime || ''}
            totalReplyCount={totalReplyCount}
            isReplyPanel
          />
          {!!commentActionsList.length && (
            <>
              <PostActions
                actionsList={commentActionsList}
                className={cx('comment__actions')}
                t={t}
              />
              <Mutation mutation={DELETE_REPLY_MUTATION}>
                {(mutate, { loading }) => (
                  <ConfirmDeleteModal
                    closeAction={closeModal}
                    confirmDeleteBody={confirmDeleteBody}
                    confirmDeleteTitle={confirmDeleteTitle}
                    deleteAction={handleConfirmDelete(mutate)}
                    isConfirmModalOpen={isOpen}
                    isLoading={loading}
                  />
                )}
              </Mutation>
            </>
          )}
        </div>

        <div className={cx('comment__content', { 'is-reply': isReply })} data-qa="comment__content">
          <ExpandableContent
            attachments={(comment.postAttachments as Attachment[]) || []}
            body={comment?.body as PostBody}
            hasAttachment={!!hasAttachment}
            id={comment.id}
            t={t}
            isComment
          />
          {multipleReactionsEnabled && (
            <div className={cx('comment__reaction-summary')}>
              <ReactionSummaryDisplay
                myReactions={myReactions}
                reactionStats={reactionStats}
                onReactionClick={handleReactionClick}
              />
            </div>
          )}
          <div
            className={cx('comment__content__footer', {
              'multiple-reactions-enabled': multipleReactionsEnabled,
            })}
          >
            <div
              className={cx('comment__content__buttons-wrapper')}
              data-qa="comment__content__buttons-wrapper"
            >
              {reactionComponent}
              {!isReply &&
                (replyButtonDisabled ? (
                  <Tooltip label={t('components-postFooter-read_only_tooltip', 'Read only')}>
                    {replyButton({ disabled: true })}
                  </Tooltip>
                ) : (
                  replyButton({ disabled: false })
                ))}
            </div>
            {!parentReplyId && (
              <ReplyThread
                author={user}
                fetchMoreReplies={fetchMoreReplies}
                hasReplies={hasReplies}
                isFollowingThread={isFollowingThread}
                isReadOnlySpace={isReadOnlySpace}
                parentPostId={parentPostId}
                parentReplyId={comment.id}
                replies={comment.replies}
                t={t}
                onPostChange={onPostChange}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div
      className={cx('comment', { reply: isReply })}
      data-qa={`Comment-${comment.id}`}
      ref={commentRef}
    >
      <div className={cx('comment__container')}>{commentContent}</div>
    </div>
  );
};

export default Comment;
